import React from 'react';
import { Box, Stack, Toolbar, Typography } from '@mui/material';
import { NavigationHeader } from '_lex/_atoms/NavigationHeader';
import Sidebar, { NavItem } from '_lex/_atoms/Sidebar';
import { PageHeader } from '_lex/_atoms/PageHeader/PageHeader';
import RightDrawer from '_lex/_layouts/TakeoverLayout/RightDrawer';
import Tabs, { TabsProps } from '_lex/_atoms/Tabs';

export interface DefaultLayoutProps {
  children?: React.ReactNode;
  footerContent?: React.ReactNode;
  pageTitle?: React.ReactNode;
  navigationHeader?: React.ReactNode;
  pageActions?: React.ReactNode;
  rightDrawer?: {
    header?: React.ReactNode;
    content: React.ReactNode;
    footer?: React.ReactNode;
    closeable?: boolean;
  };
  noPadding?: boolean;
  sidebarItems?: NavItem[];
  onSidebarNavigate?: (item: NavItem) => void;
  headerProps?: {
    title: React.ReactNode;
    titleBadge?: React.ReactNode;
    subtitle?: React.ReactNode;
    actions?: React.ReactNode;
    breadcrumbItems?: React.ReactNode[];
  };
  tabs?: TabsProps;
}

export const DefaultLayout: React.FC<DefaultLayoutProps> = ({
  children = [],
  footerContent,
  pageTitle,
  navigationHeader,
  pageActions,
  rightDrawer,
  noPadding = false,
  sidebarItems = [],
  onSidebarNavigate,
  headerProps,
  tabs,
}: DefaultLayoutProps) => {
  const [rightDrawerOpen, setRightDrawerOpen] = React.useState(true);

  const gridColumns = () => {
    let gridCols = 'minmax(0, 1fr)';
    if (sidebarItems.length > 0) gridCols = `72px ${gridCols}`;
    if (!!rightDrawer) gridCols = `${gridCols} auto`;

    return gridCols;
  };

  const contentRows = () => {
    let gridRows = '1fr';
    if (headerProps) gridRows = `auto ${gridRows}`;
    if (tabs) gridRows = `auto ${gridRows}`;
    if (footerContent) gridRows = `${gridRows} auto`;

    return gridRows;
  };

  return (
    <Box
      className='lex-detail_page'
      sx={{
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        gridTemplateColumns: 'minmax(0, 1fr)',
        height: '100vh',
        maxHeight: '100vh',
      }}
    >
      {navigationHeader ?? (
        <NavigationHeader
          global={true}
          leftItems={<Typography variant='subtitleSm'>{pageTitle}</Typography>}
          rightItems={pageActions}
        />
      )}
      <Box
        className='lex-detail_container'
        sx={{
          display: 'grid',
          gridTemplateColumns: gridColumns(),
          gridRow: '2',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {sidebarItems.length > 0 && (
          <Box
            sx={{
              position: 'relative',
              height: '100%',
              gridRow: '1 / -1',
            }}
          >
            <Sidebar
              items={sidebarItems}
              onNavigate={onSidebarNavigate}
            />
          </Box>
        )}
        <Box
          className='lex-detail_main'
          sx={{
            background: '#FFF',
            display: 'grid',
            gridTemplateRows: contentRows(),
            gridTemplateColumns: 'minmax(0, 1fr)',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {headerProps && (
            <PageHeader
              {...headerProps}
              actions={
                Array.isArray(headerProps.actions) ? (
                  <Stack
                    direction='row'
                    spacing={2}
                  >
                    {headerProps.actions}
                  </Stack>
                ) : (
                  headerProps.actions
                )
              }
            />
          )}
          {tabs && (
            <Box
              className='lex-tabs'
              sx={{ px: 4 }}
            >
              <Tabs {...tabs} />
            </Box>
          )}
          <Box
            className='lex-detail_content'
            sx={{
              minHeight: 0,
              margin: '0 auto',
              display: 'grid',
              gridTemplateRows: '1fr',
              rowGap: noPadding ? 0 : 4,
              p: noPadding ? 0 : 4,
              width: '100%',
              overflow: 'auto',
            }}
          >
            {children}
          </Box>
          {footerContent && (
            <Toolbar
              className='lex-panel-header'
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack
                direction='row'
                spacing={2}
                alignItems='center'
                sx={{ flexGrow: 1 }}
              >
                {footerContent}
              </Stack>
            </Toolbar>
          )}
        </Box>
        {!!rightDrawer && (
          <RightDrawer
            {...rightDrawer}
            open={rightDrawerOpen}
            setOpen={rightDrawer.closeable !== false ? setRightDrawerOpen : undefined}
          />
        )}
      </Box>
    </Box>
  );
};

export default DefaultLayout;
